import React, { useState } from 'react';
import { Plus, X, GlassWater, CupSoda, Blend } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { IMAGES, money } from '../lib/constants';

export const DRINKS = {
  sodas: {
    label: 'Gaseosas',
    category: 'Bebidas',
    icon: CupSoda,
    price: 25,
    desc: 'Bebida gaseosa bien fría',
    image: IMAGES.soda,
    flavors: ['Coca-Cola', 'Coca-Cola Zero', 'Pepsi', 'Fanta Naranja', 'Sprite', 'Tropical Uva', 'Té frío', 'Agua purificada'],
    sizes: [['12 oz', 0], ['20 oz', 10], ['1 litro', 25], ['2 litros', 45]],
  },
  juices: {
    label: 'Jugos y Licuados',
    category: 'Jugos y Licuados',
    icon: GlassWater,
    price: 40,
    desc: 'Jugo natural recién hecho',
    image: IMAGES.juice,
    flavors: ['Naranja', 'Piña', 'Sandía', 'Limonada', 'Tamarindo', 'Maracuyá', 'Horchata', 'Zanahoria con naranja'],
    sizes: [['12 oz', 0], ['16 oz', 12], ['1 litro', 30]],
  },
  smoothies: {
    label: 'Licuados',
    category: 'Jugos y Licuados',
    icon: Blend,
    price: 60,
    desc: 'Licuado de frutas con leche o agua',
    image: IMAGES.juice,
    flavors: ['Banano con leche', 'Fresa', 'Mango', 'Papaya', 'Mora', 'Tres frutas', 'Avena con banano', 'Chocobanano'],
    sizes: [['16 oz', 0], ['20 oz', 15], ['24 oz', 25]],
  },
};

const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');

// Quick-add dialog: pick the sodas / natural juices the restaurant sells and
// they are created as real menu products (with optional size options).
export const DrinksQuickAdd = ({ open, onClose, menu, onSave }) => {
  const [kind, setKind] = useState('sodas');
  const preset = DRINKS[kind];
  const [picked, setPicked] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [price, setPrice] = useState(DRINKS.sodas.price);
  const [busy, setBusy] = useState(false);

  const switchKind = (k) => { setKind(k); setPicked([]); setSizes([]); setPrice(DRINKS[k].price); };
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const add = async () => {
    if (!picked.length) return toast.error('Selecciona al menos una bebida');
    const p = Number(price);
    if (!p) return toast.error('Ingresa un precio');
    const existing = new Set(menu.items.map((i) => `${i.category}|${i.name}`.toLowerCase()));
    const sizeOptions = preset.sizes.filter(([n]) => sizes.includes(n)).map(([n, extra]) => ({ id: slug(n), name: n, price: extra }));
    const fresh = picked
      .filter((name) => !existing.has(`${preset.category}|${name}`.toLowerCase()))
      .map((name, idx) => ({
        id: `it${Date.now()}${idx}`,
        name,
        desc: preset.desc,
        price: p,
        category: preset.category,
        image: preset.image,
        active: true,
        sizes: sizeOptions,
        options: [],
      }));
    if (!fresh.length) return toast('Esas bebidas ya están en el menú');
    const categories = menu.categories.includes(preset.category) ? menu.categories : [...menu.categories, preset.category];
    setBusy(true);
    try {
      await onSave({ categories, items: [...menu.items, ...fresh] });
      toast.success(`${fresh.length} bebidas agregadas a ${preset.category}`);
      setPicked([]);
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="drinks-quick-add">
        <DialogHeader><DialogTitle>Agregar bebidas</DialogTitle><DialogDescription>Elige las bebidas que vendes y se agregan a tu menú al instante.</DialogDescription></DialogHeader>
        <div className="flex gap-2">
          {Object.entries(DRINKS).map(([k, d]) => (
            <button key={k} data-testid={`drinks-tab-${k}`} onClick={() => switchKind(k)} className={`flex-1 h-10 rounded-xl text-sm font-bold flex items-center justify-center gap-2 border transition-colors ${kind === k ? 'bg-[#FDECEC] border-[#EE3B36] text-[#EE3B36]' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <d.icon size={16} /> {d.label}
            </button>
          ))}
        </div>

        <div className="mt-1">
          <Label className="text-xs font-semibold text-gray-600">Sabores que vendes</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {preset.flavors.map((f) => (
              <button key={f} data-testid={`drink-${slug(f)}`} onClick={() => toggle(picked, setPicked, f)} className={`h-10 px-3 rounded-xl text-[13px] font-semibold border text-left truncate transition-colors ${picked.includes(f) ? 'bg-[#EE3B36] border-[#EE3B36] text-white' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>{f}</button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-600">Precio base (L)</Label>
            <Input data-testid="drinks-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-600">Tamaños (opcional)</Label>
            <div className="flex flex-wrap gap-1.5">
              {preset.sizes.map(([n, extra]) => (
                <button key={n} data-testid={`drink-size-${slug(n)}`} onClick={() => toggle(sizes, setSizes, n)} className={`h-8 px-2.5 rounded-lg text-[12px] font-bold border ${sizes.includes(n) ? 'bg-[#1E2130] border-[#1E2130] text-white' : 'bg-white border-gray-200 text-gray-600'}`}>{n}{extra ? ` +${money(extra)}` : ''}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button data-testid="drinks-save" disabled={busy} className="bg-[#EE3B36] hover:bg-[#D93129] gap-2" onClick={add}><Plus size={16} /> Agregar {picked.length ? `(${picked.length})` : ''}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Editor for product flavours / sizes (each variant can add a surcharge).
export const VariantEditor = ({ label, hint, placeholder = 'Ej. Coca-Cola', value = [], onChange, testId = 'variants' }) => {
  const [name, setName] = useState('');
  const [extra, setExtra] = useState('');
  const add = () => {
    const n = name.trim();
    if (!n) return;
    if (value.some((v) => v.name.toLowerCase() === n.toLowerCase())) { setName(''); return; }
    onChange([...value, { id: `${slug(n)}-${value.length}`, name: n, price: Number(extra) || 0 }]);
    setName('');
    setExtra('');
  };
  return (
    <div data-testid={testId}>
      <Label className="text-xs font-semibold text-gray-600">{label}</Label>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {value.map((v) => (
            <span key={v.id} data-testid={`${testId}-chip-${slug(v.name)}`} className="inline-flex items-center gap-1.5 bg-[#F7F8FA] border border-gray-200 rounded-full pl-3 pr-1.5 py-1 text-[12px] font-semibold">
              {v.name}{v.price ? <span className="text-[#EE3B36]">+{money(v.price)}</span> : null}
              <button onClick={() => onChange(value.filter((x) => x.id !== v.id))} className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center hover:bg-red-100 hover:text-red-600"><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2 mt-1.5">
        <Input data-testid={`${testId}-name`} value={name} placeholder={placeholder} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} className="h-9" />
        <Input data-testid={`${testId}-extra`} value={extra} type="number" placeholder="+L" onChange={(e) => setExtra(e.target.value)} className="h-9 w-20" />
        <Button data-testid={`${testId}-add`} type="button" variant="outline" className="h-9 shrink-0" onClick={add}><Plus size={15} /></Button>
      </div>
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
};
